import React, { useEffect, useRef } from 'react';

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptLoadingPromise;

function loadTurnstileScript() {
  if (typeof window !== 'undefined' && window.turnstile) {
    return Promise.resolve();
  }
  if (scriptLoadingPromise) {
    return scriptLoadingPromise;
  }
  scriptLoadingPromise = new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${TURNSTILE_SRC}"]`);
    if (existing) {
      if (window.turnstile) {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve());
      }
      return;
    }
    const script = document.createElement('script');
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    document.head.appendChild(script);
  });
  return scriptLoadingPromise;
}

const Turnstile = ({ siteKey, onVerify, onExpire, theme = 'auto', action = 'signup' }) => {
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);
  const verifyRef = useRef(onVerify);
  const expireRef = useRef(onExpire);

  // Keep latest callbacks without re-rendering the widget
  useEffect(() => {
    verifyRef.current = onVerify;
  }, [onVerify]);
  useEffect(() => {
    expireRef.current = onExpire;
  }, [onExpire]);

  useEffect(() => {
    let isMounted = true;
    loadTurnstileScript().then(() => {
      if (!isMounted || !widgetRef.current || !window.turnstile || !siteKey) return;
      try {
        if (widgetIdRef.current) return; // already rendered, avoid re-render flashing
        widgetIdRef.current = window.turnstile.render(widgetRef.current, {
          sitekey: siteKey,
          theme,
          action,
          callback: (token) => verifyRef.current && verifyRef.current(token),
          'expired-callback': () => expireRef.current && expireRef.current(),
          'error-callback': () => expireRef.current && expireRef.current(),
        });
      } catch (e) {
        // ignore
      }
    });
    return () => {
      isMounted = false;
      try {
        // Do not aggressively remove on routine re-renders; only on unmount
        if (widgetIdRef.current && window.turnstile) window.turnstile.remove(widgetIdRef.current);
      } catch (e) {
        // ignore
      }
    };
  }, [siteKey, theme, action]);

  return (
    <div style={{ display: 'inline-block', minHeight: 70 }}>
      <div ref={widgetRef} />
    </div>
  );
};

export default Turnstile;


